/**
 * @file useReferencedListConfig.ts
 * @description Client read of the single `parameters/referencedList` document — the admin
 * declaration of which (source → target) relations the reverse index maintains.
 *
 * Consumers: the Referenced Lists settings tab (declares and withdraws relations) and
 * `AddElementMenu` (disables the Referenced By layout element on an entity no relation targets).
 * The store is the shared singleton for that path, so both mount one listener between them.
 *
 * Invariant: `reconciliation` is job-owned. Nothing on the client writes it; the settings tab
 * writes `relations` and `revision` only. Parse the returned document through
 * `parseReferencedListRelations` / `parseReferencedListTargets` rather than reading `relations`
 * directly — a pair the reconciler rejected is still declared.
 *
 * Invariant: an absent `config` means "unknown" while `loading` is true and "no relations declared"
 * only once it is false — a consumer that asserts either way without reading `loading` is wrong on
 * first render.
 */

import { useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { createDocumentStore } from '../store';
import { REFERENCED_LIST_CONFIG_PATH, type ReferencedListConfig } from '#types';

export const referencedListConfigStore = createDocumentStore<
    ReferencedListConfig & Record<string, unknown>
>(REFERENCED_LIST_CONFIG_PATH);

export const useReferencedListConfig = () => {
    useEffect(() => {
        void referencedListConfigStore.connect();
        return () => referencedListConfigStore.disconnect();
    }, []);

    const snap = useSnapshot(referencedListConfigStore.state);

    return {
        config: (snap.data as ReferencedListConfig | null) ?? undefined,
        loading: snap.loading,
    };
};
